import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, HelpCircle, Info, X, XCircle } from 'lucide-react';

/**
 * 공통 알림 — 토스트(showToast) · 확인창(showConfirm). GRK_admin 에서 옮겼다(contexts/AlertContext.tsx, 2026-11-02) —
 * 고칠 때 두 곳을 같이 본다. 모양만 이 앱 색(indigo)에 맞췄다.
 *
 * 브라우저 기본 창(window.confirm · alert · prompt)은 쓰지 않는다 — 화면이 멈추고(자동 점검 도구도 막힌다),
 * 모양이 앱과 다르다. 확인이 필요하면 `const { showConfirm } = useAlert(); if (!(await showConfirm('…'))) return;`
 */
type ToastType = 'success' | 'error' | 'info' | 'warning';
interface Toast { id: number; type: ToastType; message: string }
interface ConfirmOptions { title?: string; confirmText?: string; danger?: boolean }
interface ConfirmState extends ConfirmOptions { message: string; resolve: (ok: boolean) => void }

interface AlertContextType {
  showToast: (message: string, type?: ToastType) => void;
  showConfirm: (message: string, opts?: ConfirmOptions) => Promise<boolean>;
}

const AlertContext = createContext<AlertContextType | null>(null);

export const useAlert = () => {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error('useAlert 는 AlertProvider 안에서만 씁니다');
  return ctx;
};

const STYLE: Record<ToastType, { box: string; Icon: typeof Info }> = {
  success: { box: 'bg-emerald-600', Icon: CheckCircle2 },
  error: { box: 'bg-red-600', Icon: XCircle },
  info: { box: 'bg-slate-800', Icon: Info },
  warning: { box: 'bg-amber-600', Icon: AlertTriangle },
};

const ToastItem: React.FC<{ toast: Toast; onRemove: (id: number) => void }> = ({ toast, onRemove }) => {
  // 오류는 더 오래 — 읽기 전에 사라지면 무엇이 틀렸는지 모른다
  useEffect(() => {
    const t = setTimeout(() => onRemove(toast.id), toast.type === 'error' ? 8000 : 3000);
    return () => clearTimeout(t);
  }, [toast.id, toast.type, onRemove]);
  const { box, Icon } = STYLE[toast.type];
  return (
    <div role={toast.type === 'error' ? 'alert' : 'status'}
      className={`${box} text-white px-4 py-3 rounded-lg shadow-lg flex items-start gap-3 min-w-[280px] max-w-[420px] animate-slide-in`}>
      <Icon size={18} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
      <span className="flex-1 text-sm whitespace-pre-line">{toast.message}</span>
      <button onClick={() => onRemove(toast.id)} aria-label="닫기" className="text-white/80 hover:text-white"><X size={16} /></button>
    </div>
  );
};

const ConfirmModal: React.FC<{ state: ConfirmState; onDone: (ok: boolean) => void }> = ({ state, onDone }) => {
  const okRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    okRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onDone(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDone]);
  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-[9999] p-4" onClick={() => onDone(false)}>
      <div role="alertdialog" aria-modal="true" aria-labelledby="confirm-title"
        className="bg-white rounded-lg shadow-xl w-[400px] max-w-full overflow-hidden animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 flex items-start gap-3">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${state.danger ? 'bg-red-50 text-red-600' : 'bg-indigo-50 text-indigo-600'}`}>
            {state.danger ? <AlertTriangle size={18} /> : <HelpCircle size={18} />}
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="confirm-title" className="text-base font-semibold text-slate-900 mb-1">{state.title ?? '확인'}</h3>
            <p className="text-sm text-slate-600 whitespace-pre-line">{state.message}</p>
          </div>
        </div>
        <div className="flex border-t border-slate-100">
          <button onClick={() => onDone(false)} className="flex-1 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50">취소</button>
          <button ref={okRef} onClick={() => onDone(true)}
            className={`flex-1 py-3 text-sm font-medium text-white ${state.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
            {state.confirmText ?? '확인'}
          </button>
        </div>
      </div>
    </div>
  );
};

let seq = 0;

export const AlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    setToasts((xs) => [...xs, { id: ++seq, type, message }]);
  }, []);
  const removeToast = useCallback((id: number) => setToasts((xs) => xs.filter((t) => t.id !== id)), []);

  const showConfirm = useCallback((message: string, opts: ConfirmOptions = {}) =>
    new Promise<boolean>((resolve) => setConfirm({ message, ...opts, resolve })), []);
  const done = useCallback((ok: boolean) => {
    setConfirm((c) => { c?.resolve(ok); return null; });
  }, []);

  // 화면이 받지 못한 실패(서버가 꺼졌다 등)는 오류 토스트로 — 아무 말 없이 멈추지 않게
  useEffect(() => {
    const onReject = (e: PromiseRejectionEvent) => showToast(String((e.reason as Error)?.message ?? e.reason), 'error');
    const onApp = (e: Event) => showToast(String((e as CustomEvent).detail), 'error');
    window.addEventListener('unhandledrejection', onReject);
    window.addEventListener('app:error', onApp);
    return () => { window.removeEventListener('unhandledrejection', onReject); window.removeEventListener('app:error', onApp); };
  }, [showToast]);

  return (
    <AlertContext.Provider value={{ showToast, showConfirm }}>
      {children}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2" aria-live="polite">
        {toasts.map((t) => <ToastItem key={t.id} toast={t} onRemove={removeToast} />)}
      </div>
      {confirm && <ConfirmModal state={confirm} onDone={done} />}
    </AlertContext.Provider>
  );
};
